import { formFieldSanitisedText } from './sanitised-text.js';
/**
 * Sanitise an SA phone number to canonical E.164 form: +27XXXXXXXXX.
 *
 * Strips every character that is not a digit or `+`, then normalises:
 * - leading `0` becomes `+27`
 * - leading `27` (no `+`) becomes `+27`
 *
 * Returns the canonical form, or `null` if the input cannot be coerced
 * into a valid SA mobile/landline number (must be exactly `+27` followed
 * by 9 digits).
 */
export function sanitiseSouthAfricanPhoneNumber(input) {
    if (input.length > 30)
        return null;
    const stripped = input.replace(/[^\d+]/g, '');
    let candidate;
    if (stripped.startsWith('+')) {
        candidate = stripped;
    }
    else if (stripped.startsWith('0')) {
        candidate = `+27${stripped.slice(1)}`;
    }
    else if (stripped.startsWith('27')) {
        candidate = `+${stripped}`;
    }
    else {
        return null;
    }
    return /^\+27\d{9}$/.test(candidate) ? candidate : null;
}
// =============================================================================
// Phone number form field schema
// =============================================================================
/**
 * Zod schema for an optional SA phone number on a react-hook-form form field.
 *
 * Accepts an empty string (the field is optional on every consumer) and
 * otherwise sanitises and validates, parsing to the canonical `+27XXXXXXXXX`
 * form so the value the form submits is already clean. A single transform
 * (rather than a union) so the custom message surfaces directly as the field
 * error. The message is static so `fromZodError` never reflects user-supplied
 * values.
 *
 * The strict wire schema `saPhoneNumberSchema` (api/common) shares the same
 * sanitiser; use this one only at the form boundary where blank is allowed.
 */
export const phoneNumberFormFieldSchema = formFieldSanitisedText(sanitiseSouthAfricanPhoneNumber, 'Please enter a valid SA phone number, e.g. +27 82 123 4567');
